import {
    EventSummary,
} from "../drinkService";
import {AuthUser} from "../../context/AuthContext";
import {
    ActiveEventResponse,
    CreateEventRequest,
    EventVisibility,
    EventMembershipRequest,
    ListEventsRequest,
    UpdateEventAccessRequest,
} from "../../generated/drinks_pb";
import {DrinkServiceLolPromiseClient} from "../../generated/drinks_grpc_web_pb";
import GRPC_BACKEND_URL, { GRPC_CLIENT_OPTIONS } from "../../grpcConfig";
import {buildUserMessage} from "./utils/user";
import {mapEvent, mapEventList} from "./mappers";

type EventCreationOptions = {
    visibility?: EventVisibility;
    password?: string | null;
};

type EventAccessUpdatePayload = {
    visibility: EventVisibility;
    password?: string | null;
};
const drinkClient = new DrinkServiceLolPromiseClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);


export const fetchActiveEvent = async (user: AuthUser): Promise<EventSummary | null> => {
    const response: ActiveEventResponse = await drinkClient.getActiveEvent(buildUserMessage(user), {});
    const event = response.getEvent();
    return event ? mapEvent(event) : null;
};

export const fetchOpenEvents = async (user: AuthUser): Promise<EventSummary[]> => {
    const list = await drinkClient.listOpenEvents(buildUserMessage(user), {});
    return mapEventList(list);
};

export const fetchAllEvents = async (user?: AuthUser, includeInactive = true): Promise<EventSummary[]> => {
    const request = new ListEventsRequest();
    request.setIncludeinactive(includeInactive);
    if (user) {
        request.setUser(buildUserMessage(user));
    }

    const list = await drinkClient.getEvents(request, {});
    return mapEventList(list);
};

export const createEvent = async (
    user: AuthUser,
    eventName: string,
    location: string,
    options?: EventCreationOptions
): Promise<EventSummary> => {
    const request = new CreateEventRequest();
    request.setUser(buildUserMessage(user));
    request.setEventname(eventName);
    request.setLocation(location);
    request.setVisibility(options?.visibility ?? EventVisibility.EVENT_VISIBILITY_PUBLIC);

    if (options?.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD && options.password) {
        request.setAccesspassword(options.password);
    } else if (options?.password) {
        request.setAccesspassword(options.password);
    } else {
        request.clearAccesspassword();
    }

    const response = await drinkClient.createEvent(request, {});
    return mapEvent(response);
};

export const joinEvent = async (user: AuthUser, event: EventSummary, password?: string): Promise<EventSummary> => {
    const response = await drinkClient.joinEvent(
        buildMembershipRequest(user, event.eventId, password),
        {}
    );
    return mapEvent(response);
};

export const leaveEvent = async (user: AuthUser, eventId: number): Promise<EventSummary> => {
    const response = await drinkClient.leaveEvent(buildMembershipRequest(user, eventId), {});
    return mapEvent(response);
};

export const endEvent = async (user: AuthUser, eventId: number): Promise<EventSummary> => {
    const response = await drinkClient.endEvent(buildMembershipRequest(user, eventId), {});
    return mapEvent(response);
};

export const deleteEvent = async (user: AuthUser, eventId: number): Promise<void> => {
    await drinkClient.deleteEvent(buildMembershipRequest(user, eventId), {});
};

export const updateEventAccess = async (
    user: AuthUser,
    eventId: number,
    payload: EventAccessUpdatePayload
): Promise<EventSummary> => {
    const request = new UpdateEventAccessRequest();
    request.setUser(buildUserMessage(user));
    request.setEventid(eventId);
    request.setVisibility(payload.visibility);

    if (payload.password !== undefined) {
        if (payload.password === null || payload.password === "") {
            request.clearAccesspassword();
        } else {
            request.setAccesspassword(payload.password);
        }
    }

    const response = await drinkClient.updateEventAccess(request, {});
    return mapEvent(response);
};

const buildMembershipRequest = (user: AuthUser, eventId: number, password?: string): EventMembershipRequest => {
    const req = new EventMembershipRequest();
    req.setUser(buildUserMessage(user));
    req.setEventid(eventId);
    if (password) {
        req.setAccesspassword(password);
    }
    return req;
};


